import { call, put, select, take, takeLatest, takeLeading } from 'redux-saga/effects'
import { PRELOAD_DATA_LOCAL_STORAGE_KEY } from 'constant/common'
import { INIT_APP } from 'redux/constant/app'
import {
  selectIsShowingFullPageError,
  selectIsShowingLaunchSplash,
  selectIsShowingMaintenancePage,
  selectIsViewedAdvertisement,
} from 'redux/selector/app'
import { type PreloadShare, selectPreloadShare } from 'redux/selector/preloadConfig'
import { actions } from 'redux/slice/app'
import { preloadConfigActions } from 'redux/slice/preloadConfig'
import { getFastestOriginWithDataValOrNull } from 'utils/fastestSpeed'
import { getMetadataJSONWithUrl, getMetadataJSONWithUrlWithCache } from 'utils/id-util/update-util'
import { isValidHttpUrl } from 'utils/isValidHttpUrl'
import { waitForIdle } from 'utils/waitForIdle'

/** 外部注入的 meta config，本 saga 只取用分享相關的兩個欄位 */
interface PreloadConfig {
  'ori-share'?: unknown
  'web-share'?: unknown
}

const toValidUrlList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string' && isValidHttpUrl(item))
}

function* fetchPreloadConfigSaga(): Generator {
  try {
    const preloadPath = process.env.REACT_APP_PRELOAD_PATH
    if (!preloadPath) {
      yield put(preloadConfigActions.fetchPreloadConfigFailure('preload path is not defined'))
      return
    }

    const config = (yield call(
      getMetadataJSONWithUrlWithCache,
      preloadPath,
      PRELOAD_DATA_LOCAL_STORAGE_KEY
    )) as PreloadConfig | null

    const oriShare = toValidUrlList(config?.['ori-share'])
    const webShare = toValidUrlList(config?.['web-share'])

    yield put(preloadConfigActions.setPreloadConfig({ oriShare, webShare }))

    // 有可測的網域才啟動測速；reducer 的 idle → testing guard 會擋掉重複派發。
    if (oriShare.length > 0) {
      yield put(preloadConfigActions.startOriShareSpeedTest())
    }
  } catch (error) {
    yield put(preloadConfigActions.fetchPreloadConfigFailure(error instanceof Error ? error.message : undefined))
  }
}

/**
 * INIT_APP 只負責「發出 fetchPreloadConfig action」而非直接做事，
 * 這樣 reducer 才會把 loading 設為 true；否則等待方會看到
 * loading=false / loaded=false 的空窗期而誤判成「沒人在載入」。
 */
function* triggerPreloadConfigSaga(): Generator {
  yield put(preloadConfigActions.fetchPreloadConfig())
}

/**
 * 等待首頁真正呈現。
 *
 * 口徑直接沿用專案既有的 T2 定義（hooks/useAnalytics.ts 的 useEntryLoadReport），不自創：
 * 只看 isShowingLaunchSplash 是不夠的——載入快取入站廣告時會立刻關閉啟動頁
 * （redux/saga/app.ts 的 loadCachedAd），此時廣告仍在畫面上；維護頁與全頁錯誤路徑同樣會先關啟動頁。
 *
 * 回傳 false 代表本 session 不該測速（已落入維護頁或全頁錯誤），呼叫端據此收尾。
 */
function* waitForHomeVisible(): Generator<unknown, boolean, unknown> {
  while (true) {
    const isViewedAd = (yield select(selectIsViewedAdvertisement)) as boolean
    const showSplash = (yield select(selectIsShowingLaunchSplash)) as boolean
    const showMaintenance = (yield select(selectIsShowingMaintenancePage)) as boolean
    const showFullPageError = (yield select(selectIsShowingFullPageError)) as boolean

    // 全頁阻擋態：使用者看不到首頁，測速沒有意義，且重試本身就會 reload。
    if (showMaintenance || showFullPageError) return false
    if (isViewedAd && !showSplash) return true

    // 四個狀態都可能是下一個變化來源，缺一就可能漏醒：
    // 例如啟動頁先關、稍後才進維護頁的路徑，只等啟動頁 action 會永遠醒不過來。
    yield take([
      actions.updateViewedAdvertisement.type,
      actions.updateIsShowingLaunchSplash.type,
      actions.updateMaintenanceSplash.type,
      actions.updateFullPageError.type,
    ])
  }
}

/**
 * ori-share 線路測速。
 *
 * 時機：首頁呈現後再等瀏覽器閒置，避免與首屏搶主執行緒與網路併發。
 * 清單快照刻意等到兩道閘都通過後才取：若改用派發當下的快照，期間背景刷新換了清單，
 * 測出來的網域會不在新清單裡，取用端的 includes 檢查必然落空，而本 session 又不會重測。
 */
function* oriShareSpeedTestSaga(): Generator {
  try {
    // 第二道保險：reducer 已擋過一次，這裡再確認自己確實是被授權的那一輪。
    const current = (yield select(selectPreloadShare)) as PreloadShare
    if (current.oriShareSpeedTest.status !== 'testing') return

    const homeVisible = (yield call(waitForHomeVisible)) as boolean
    if (!homeVisible) {
      yield put(preloadConfigActions.setOriShareSpeedTestResult(null))
      return
    }

    yield call(waitForIdle)

    const preload = (yield select(selectPreloadShare)) as PreloadShare
    const fastest = (yield call(getFastestOriginWithDataValOrNull, preload.oriShare)) as string | null

    yield put(preloadConfigActions.setOriShareSpeedTestResult(fastest))
  } catch (error) {
    // 任何例外都必須落到終局，否則狀態卡在 testing，往後每次分享都降級且永不恢復。
    console.warn('[oriShareSpeedTest] failed, falling back to random pick', error)
    yield put(preloadConfigActions.setOriShareSpeedTestResult(null))
  }
}

/**
 * 背景刷新：不動 loading / loaded，只在確實從網路取得有效資料時才覆寫既有清單。
 *
 * 存在理由：啟動時若網路暫時失敗，getMetadataJSONWithUrlWithCache 會回退讀
 * localStorage 快取。該快取可能含「語法合法但已下線」的舊網域——陣列非空，
 * 因此不會觸發 shareVideo 的空清單 retry。而稍後恢復連線時，既有的
 * precachePreloadConfig（app.ts:271）只更新 localStorage、不碰 Redux，
 * 舊網域就會沿用整個 session。此 saga 讓分享流程能自我修復。
 */
function* refreshPreloadConfigSaga(): Generator {
  try {
    const preloadPath = process.env.REACT_APP_PRELOAD_PATH
    if (!preloadPath) return

    // 刻意用 network-only 版本，不可用 getMetadataJSONWithUrlWithCache：
    // 後者在網路失敗時會回退讀 localStorage，而該快取可能比 Redux 現有資料更舊
    //（成功路徑不會更新快取，見 update-util.ts:513），
    // 那會把有效的新網域「回滾」成舊網域。網路失敗時就該保持現狀，
    // 因此讓例外落到 catch。cache helper 本來也是先打同一個網路請求，故請求數不變。
    const config = (yield call(getMetadataJSONWithUrl, preloadPath)) as PreloadConfig | null

    const oriShare = toValidUrlList(config?.['ori-share'])
    const webShare = toValidUrlList(config?.['web-share'])

    // 只在兩份清單都有效時才覆寫，避免用更差的結果蓋掉目前可用的資料
    if (oriShare.length > 0 && webShare.length > 0) {
      yield put(preloadConfigActions.setPreloadConfig({ oriShare, webShare }))
    }
  } catch {
    // 網路失敗才會落到這裡，此時完全不動既有狀態，維持現有清單繼續可用
  }
}

export default function* preloadConfigSaga() {
  yield takeLatest(INIT_APP, triggerPreloadConfigSaga)
  yield takeLatest(preloadConfigActions.fetchPreloadConfig.type, fetchPreloadConfigSaga)
  yield takeLatest(preloadConfigActions.refreshPreloadConfig.type, refreshPreloadConfigSaga)
  // takeLeading 是第三道保險（擋同一 tick 內的併發），真正的一次性由 reducer guard 保證。
  yield takeLeading(preloadConfigActions.startOriShareSpeedTest.type, oriShareSpeedTestSaga)
}
